<script setup>
import { ref, computed, watch } from 'vue'
import { Head, router, useForm } from '@inertiajs/vue3'
import {
    PhCalendarPlus, PhPencilSimple, PhPower, PhX, PhCaretRight,
    PhCalendarBlank, PhArrowRight,
} from '@phosphor-icons/vue'

import Layout  from '@pages/Shared/layouts/AdminLayout.vue'
import Modal   from '@pages/Shared/Modal.vue'
import Input   from '@pages/Shared/form/Input.vue'
import Table   from '@pages/Shared/ui/Table.vue'
import Prompt  from '@pages/Shared/Prompt.vue'
import Spinner from '@pages/Shared/ui/Spinner.vue'

import { flashToast } from '@/toast.js'
import { formatDate  } from '@/helpers.js'

/* -------------------- PROPS -------------------- */
const props = defineProps({
    academic_sessions: Object,
    filters: Object,
})

/* -------------------- TABLE COLUMNS -------------------- */
const columns = [
    { key: 'index',      label: '#',          type: 'index' },
    { key: 'name',       label: 'Session',    type: 'slot'  },
    { key: 'start_date', label: 'Start Date', type: 'slot'  },
    { key: 'range_arrow',label: '',           type: 'slot'  },
    { key: 'end_date',   label: 'End Date',   type: 'slot'  },
    { key: 'status',     label: 'Status',     type: 'slot'  },
    { key: 'actions',    label: '',           type: 'slot'  },
]

/* -------------------- FILTERS -------------------- */
const filters = ref({ search: null, status: null, ...props.filters })

watch(() => props.filters, (val) => { filters.value = { ...val } }, { deep: true })

const applyFilters = (extra = {}) => {
    const cleaned = Object.fromEntries(
        Object.entries({ ...filters.value, ...extra })
            .filter(([_, v]) => v !== null && v !== undefined && v !== '')
    )
    router.get('/admin/academic-sessions', { ...cleaned, filter: 1 }, {
        preserveState: true, preserveScroll: true, replace: true,
    })
}

const handleSearch = (value) => { filters.value.search = value; applyFilters() }
const handleFilter = (newFilters) => { filters.value = { ...filters.value, ...newFilters }; applyFilters() }

/* -------------------- EXPANDED ROWS -------------------- */
const expandedSessionId = ref(null)
const toggleExpand = (id) => { expandedSessionId.value = expandedSessionId.value === id ? null : id }

/* -------------------- FORM -------------------- */
const openModal   = ref(false)
const isEditing   = ref(false)
const editingId   = ref(null)

const form = useForm({
    id: null,
    name: null,
    starts_at: null,
    ends_at: null,
    first_term_starts_at: null,
    first_term_ends_at: null,
    second_term_starts_at: null,
    second_term_ends_at: null,
    third_term_starts_at: null,
    third_term_ends_at: null,
})

const handleModalClose = () => {
    form.reset()
    form.clearErrors()
    isEditing.value = false
    editingId.value = null
    openModal.value = false
}

const formatForDateInput = (datetime) => datetime ? datetime.split('T')[0] : null

const editSession = (id) => {
    const session = props.academic_sessions.data.find(s => s.id === id)
    if (!session) return

    isEditing.value = true
    editingId.value = id
    form.clearErrors()

    form.id        = session.id
    form.name      = session.name
    form.starts_at = formatForDateInput(session.starts_at)
    form.ends_at   = formatForDateInput(session.ends_at)

    const firstTerm  = session.terms?.find(t => t.name === 'First Term')
    const secondTerm = session.terms?.find(t => t.name === 'Second Term')
    const thirdTerm  = session.terms?.find(t => t.name === 'Third Term')

    form.first_term_starts_at  = formatForDateInput(firstTerm?.starts_at)
    form.first_term_ends_at    = formatForDateInput(firstTerm?.ends_at)
    form.second_term_starts_at = formatForDateInput(secondTerm?.starts_at)
    form.second_term_ends_at   = formatForDateInput(secondTerm?.ends_at)
    form.third_term_starts_at  = formatForDateInput(thirdTerm?.starts_at)
    form.third_term_ends_at    = formatForDateInput(thirdTerm?.ends_at)

    openModal.value = true
}

const handleSubmit = () => {
    const options = {
        onSuccess: (page) => { handleModalClose(); flashToast(page.props.flash) },
        onError:   ()     => flashToast({ type: 'error', message: 'Check form for errors' }),
    }

    if (isEditing.value) {
        form.put(`/admin/academic-sessions/${form.id}`, options)
        return
    }

    form.post('/admin/academic-sessions', options)
}

/* -------------------- CONFIRM PROMPTS -------------------- */
const confirmModal = ref({ isShow: false, title: '', message: '', confirmText: 'Confirm', onConfirm: null })

const showConfirm = ({ title, message, confirmText, onConfirm }) => {
    confirmModal.value = { isShow: true, title, message, confirmText: confirmText ?? 'Confirm', onConfirm }
}

const confirmActivateSession = (id) => showConfirm({
    title: 'Activate Session',
    message: 'Do you want to activate this academic session?',
    confirmText: 'Activate',
    onConfirm: () => { activateSession(id); confirmModal.value.isShow = false },
})

const confirmCloseSession = (id) => showConfirm({
    title: 'Close Session',
    message: 'Do you want to close this academic session?',
    confirmText: 'Close',
    onConfirm: () => { closeSession(id); confirmModal.value.isShow = false },
})

const confirmActivateTerm = (id) => showConfirm({
    title: 'Activate Term',
    message: 'Do you want to activate this term?',
    confirmText: 'Activate',
    onConfirm: () => { activateTerm(id); confirmModal.value.isShow = false },
})

const confirmCloseTerm = (id) => showConfirm({
    title: 'Close Term',
    message: 'Do you want to close this term?',
    confirmText: 'Close',
    onConfirm: () => { closeTerm(id); confirmModal.value.isShow = false },
})

const activateSession = (id) =>
    router.patch(`/admin/academic-sessions/${id}/activate`, {}, { onSuccess: (page) => flashToast(page.props.flash) })

const closeSession = (id) =>
    router.patch(`/admin/academic-sessions/${id}/close`, {}, { onSuccess: (page) => flashToast(page.props.flash) })

const activateTerm = (id) =>
    router.patch(`/admin/academic-terms/${id}/activate`, {}, { preserveState: true, onSuccess: (page) => flashToast(page.props.flash) })

const closeTerm = (id) =>
    router.patch(`/admin/academic-terms/${id}/close`, {}, { preserveState: true, onSuccess: (page) => flashToast(page.props.flash) })

/* -------------------- HELPERS -------------------- */
const currentSession = computed(() =>
    isEditing.value ? props.academic_sessions.data.find(s => s.id === editingId.value) : null
)

const isSessionClosed = computed(() =>
    currentSession.value ? !currentSession.value.is_active : false
)

const getSessionStatus = (session) => {
    if (session.is_active) return 'Active'
    if (new Date(session.starts_at) > new Date()) return 'Upcoming'
    return 'Closed'
}

const canActivateSession = (session) => !session.is_active && session.terms?.length

const getActiveTermIndex = (terms) => terms.findIndex(t => t.is_active)

const getTermStatus = (session, terms, index, isActive) => {
    if (!session.is_active) return 'Closed'
    if (isActive) return 'Active'
    const activeIndex = getActiveTermIndex(terms)
    if (activeIndex !== -1 && index < activeIndex) return 'Closed'
    return 'Upcoming'
}

const getTermStatusClass = (session, terms, index, isActive) => {
    if (!session.is_active) return 'bg-gray-50 text-gray-500 border-gray-200'
    if (isActive) return 'bg-green-50 text-green-700 border-green-100'
    const activeIndex = getActiveTermIndex(terms)
    if (activeIndex !== -1 && index < activeIndex) return 'bg-gray-50 text-gray-500 border-gray-200'
    return 'bg-yellow-50 text-yellow-700 border-yellow-100'
}

const isTermUpcoming = (terms, index) => {
    const activeIndex = getActiveTermIndex(terms)
    return activeIndex === -1 ? index === 0 : index > activeIndex
}

watch(
    () => [form.starts_at, form.ends_at],
    ([start, end]) => {
        if (!start || !end) return
        const clamp = (date) => {
            if (!date) return null
            if (date < start) return start
            if (date > end) return end
            return date
        }
        form.first_term_starts_at  = clamp(form.first_term_starts_at)
        form.first_term_ends_at    = clamp(form.first_term_ends_at)
        form.second_term_starts_at = clamp(form.second_term_starts_at)
        form.second_term_ends_at   = clamp(form.second_term_ends_at)
        form.third_term_starts_at  = clamp(form.third_term_starts_at)
        form.third_term_ends_at    = clamp(form.third_term_ends_at)
    }
)
</script>

<template>
    <Layout>
        <Head title="Academic Sessions" />

        <!-- Confirm Prompt -->
        <Prompt
            :title="confirmModal.title"
            :message="confirmModal.message"
            :is-show="confirmModal.isShow"
            :confirm-text="confirmModal.confirmText"
            cancelText="Cancel"
            @delete="confirmModal.onConfirm && confirmModal.onConfirm()"
            @cancel="confirmModal.isShow = false"
        />

        <!-- Header -->
        <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-primary">Academic Sessions</h2>
                <p class="text-sm text-gray-500 mt-0.5">Manage academic sessions and term dates</p>
            </div>
            <button @click="openModal = true"
                class="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 text-sm font-semibold
                    text-white bg-primary hover:bg-primary-light rounded-lg transition">
                <PhCalendarPlus :size="18" /> Add Session
            </button>
        </div>

        <!-- Table -->
        <Table
            :data="academic_sessions"
            :columns="columns"
            row-key="id"
            :expanded-id="expandedSessionId"
            :exportable="true"
            :actions="{ view: false, edit: false, delete: false }"
            :filters="[
                {
                    key: 'status',
                    label: 'Status',
                    options: [
                        { label: 'All Statuses', value: null },
                        { label: 'Active',       value: 'active'   },
                        { label: 'Upcoming',     value: 'upcoming' },
                        { label: 'Closed',       value: 'closed'   },
                    ],
                    value: filters.status,
                },
            ]"
            @search="handleSearch"
            @filter="handleFilter"
        >
            <!-- Session name with expand toggle -->
            <template #name="{ row }">
                <button
                    class="flex items-center gap-1.5 font-semibold text-primary hover:text-primary-light transition"
                    @click="toggleExpand(row.id)"
                >
                    <PhCaretRight :size="14" class="transition-transform"
                        :class="expandedSessionId === row.id ? 'rotate-90' : ''" />
                    {{ row.name }}
                </button>
            </template>

            <template #start_date="{ row }">
                <span class="text-sm text-gray-700">{{ formatDate(row.starts_at) }}</span>
            </template>

            <template #range_arrow>
                <PhArrowRight :size="14" class="text-gray-400" />
            </template>

            <template #end_date="{ row }">
                <span class="text-sm text-gray-700">{{ formatDate(row.ends_at) }}</span>
            </template>

            <!-- Status badge -->
            <template #status="{ row }">
                <span class="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium rounded-full border"
                    :class="
                        getSessionStatus(row) === 'Active'   ? 'bg-green-50 text-green-700 border-green-100'   :
                        getSessionStatus(row) === 'Upcoming' ? 'bg-yellow-50 text-yellow-700 border-yellow-100' :
                        'bg-gray-50 text-gray-500 border-gray-200'
                    ">
                    <span class="w-1.5 h-1.5 rounded-full"
                        :class="
                            getSessionStatus(row) === 'Active'   ? 'bg-green-500'  :
                            getSessionStatus(row) === 'Upcoming' ? 'bg-yellow-400' :
                            'bg-gray-400'
                        ">
                    </span>
                    {{ getSessionStatus(row) }}
                </span>
            </template>

            <!-- Actions -->
            <template #actions="{ row }">
                <div class="flex items-center justify-end gap-1.5">
                    <button v-if="canActivateSession(row)"
                        @click="confirmActivateSession(row.id)"
                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium
                               text-primary bg-primary-50 hover:bg-primary-100 rounded-md border border-primary-100 transition"
                        title="Activate Session">
                        <PhPower :size="13" /> Activate
                    </button>

                    <button v-if="row.is_active"
                        @click="confirmCloseSession(row.id)"
                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium
                               text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md border border-gray-200 transition"
                        title="Close Session">
                        <PhX :size="13" /> Close
                    </button>

                    <button @click="editSession(row.id)"
                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium
                               text-green-600 bg-green-50 hover:bg-green-100 rounded-md border border-green-100 transition"
                        title="Edit Session">
                        <PhPencilSimple :size="13" /> Edit
                    </button>
                </div>
            </template>

            <!-- Expanded: terms breakdown -->
            <template #expanded="{ row }">
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div v-for="(term, index) in row.terms" :key="term.id"
                        class="bg-white border border-gray-200 rounded-lg p-4">

                        <!-- Term header -->
                        <div class="flex items-center justify-between mb-3">
                            <div class="flex items-center gap-2">
                                <PhCalendarBlank :size="14" class="text-primary shrink-0" />
                                <span class="text-sm font-semibold text-gray-800">{{ term.name }}</span>
                            </div>
                            <span class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border"
                                :class="getTermStatusClass(row, row.terms, index, term.is_active)">
                                <span class="w-1.5 h-1.5 rounded-full"
                                    :class="
                                        getTermStatus(row, row.terms, index, term.is_active) === 'Active'   ? 'bg-green-500'  :
                                        getTermStatus(row, row.terms, index, term.is_active) === 'Upcoming' ? 'bg-yellow-400' :
                                        'bg-gray-400'
                                    ">
                                </span>
                                {{ getTermStatus(row, row.terms, index, term.is_active) }}
                            </span>
                        </div>

                        <!-- Term dates -->
                        <div class="flex items-center gap-2 text-sm text-gray-600 mb-3">
                            <span>{{ formatDate(term.starts_at) }}</span>
                            <PhArrowRight :size="12" class="text-gray-400 shrink-0" />
                            <span>{{ formatDate(term.ends_at) }}</span>
                        </div>

                        <!-- Term actions -->
                        <div class="flex gap-2">
                            <button
                                v-if="!term.is_active && isTermUpcoming(row.terms, index) && row.is_active"
                                @click="confirmActivateTerm(term.id)"
                                class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium
                                       text-primary bg-primary-50 hover:bg-primary-100 rounded-md border border-primary-100 transition">
                                <PhPower :size="12" /> Activate
                            </button>

                            <button
                                v-if="term.is_active"
                                @click="confirmCloseTerm(term.id)"
                                class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium
                                       text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md border border-gray-200 transition">
                                <PhX :size="12" /> Close
                            </button>
                        </div>
                    </div>

                    <div v-if="!row.terms?.length" class="sm:col-span-3 py-4 text-center text-sm text-gray-400 italic">
                        No terms created for this session
                    </div>
                </div>
            </template>
        </Table>

        <!-- Modal -->
        <Modal
            v-model="openModal"
            :title="isEditing ? 'Edit Session' : 'Add Session'"
            size="medium"
            @close="handleModalClose"
        >
            <form @submit.prevent="handleSubmit" class="p-6 space-y-5">

                <Input
                    v-model="form.name"
                    label="Session Name"
                    type="text"
                    placeholder="e.g. 2025/2026"
                    :error="form.errors.name"
                />
                <div class="grid grid-cols-2 gap-4">
                    <Input
                        v-model="form.starts_at"
                        label="Start Date"
                        type="date"
                        :error="form.errors.starts_at"
                    />
                    <Input
                        v-model="form.ends_at"
                        label="End Date"
                        type="date"
                        :error="form.errors.ends_at"
                    />
                </div>

                <!-- Term dates (edit only) -->
                <template v-if="isEditing">
                    <div class="pt-2 border-t border-gray-100">
                        <p class="text-sm font-semibold text-gray-700 mb-4">Term Dates</p>

                        <div v-for="term in [
                            { label: 'First Term',  start: 'first_term_starts_at',  end: 'first_term_ends_at'  },
                            { label: 'Second Term', start: 'second_term_starts_at', end: 'second_term_ends_at' },
                            { label: 'Third Term',  start: 'third_term_starts_at',  end: 'third_term_ends_at'  },
                        ]" :key="term.label" class="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
                            <p class="text-xs font-medium text-gray-500 mb-3">{{ term.label }}</p>
                            <div class="grid grid-cols-2 gap-3">
                                <Input
                                    v-model="form[term.start]"
                                    label="Start Date"
                                    type="date"
                                    :error="form.errors[term.start]"
                                    :disabled="isSessionClosed"
                                />
                                <Input
                                    v-model="form[term.end]"
                                    label="End Date"
                                    type="date"
                                    :error="form.errors[term.end]"
                                    :disabled="isSessionClosed"
                                />
                            </div>
                        </div>
                    </div>
                </template>

                <button type="submit" :disabled="form.processing || (isEditing && isSessionClosed)"
                    class="w-full px-6 py-3 rounded-lg text-white text-sm font-semibold transition
                           flex justify-center items-center gap-2
                           disabled:opacity-50 disabled:cursor-not-allowed"
                    :class="isEditing ? 'bg-edit hover:bg-edit-light' : 'bg-primary hover:bg-primary-light'">
                    <Spinner v-if="form.processing" :size="14" />
                    {{ isEditing ? 'Update Session' : 'Create Session' }}
                </button>
            </form>
        </Modal>

    </Layout>
</template>